// filepath: apps/web/src/features/nav/config.ts
//
// Single source of truth for the OSD web app's information
// architecture (IA). Adding a new admin page is a 1-line change
// here + a route file under apps/web/src/routes/admin/ — no nav
// JSX to touch.
//
// The NavLink / NavSection types come from `@osd/ui/nav` so the
// shape is shared with the design system (root/osd#70 — promote
// Sidebar / SidebarSection to @osd/ui).
//
// Each section + the major links carry an `icon` (from
// `@osd/icons`) so the icon-rail collapsed sidebar still has
// something to show. Icons are decorative — the section / link
// label remains the accessible name.

import { Home } from '@osd/icons/home';
import { LayoutGrid } from '@osd/icons/layout-grid';
import { Palette } from '@osd/icons/palette';
import { Users } from '@osd/icons/users';
import { Settings } from '@osd/icons/settings';
import { UserCheck } from '@osd/icons/user-check';
import { FileText } from '@osd/icons/file-text';
import { Mail } from '@osd/icons/mail';
import type { NavSection } from '@osd/ui/nav';

export type { NavLink, NavSection } from '@osd/ui/nav';

export const NAV_SECTIONS: ReadonlyArray<NavSection> = [
  {
    id: 'main',
    label: 'Main',
    icon: <Home />,
    children: [
      { to: '/', label: 'Home', icon: <Home /> },
      { to: '/about', label: 'About' },
    ],
  },
  {
    id: 'ui-kit',
    label: 'UI Kit',
    icon: <LayoutGrid />,
    children: [
      { to: '/ui/getting-started', label: 'Getting started' },
      { to: '/ui', label: 'Components', icon: <LayoutGrid /> },
    ],
  },
  {
    id: 'icon-kit',
    label: 'Icon Kit',
    icon: <Palette />,
    children: [
      { to: '/icons/getting-started', label: 'Getting started' },
      { to: '/icons', label: 'Browse', icon: <Palette /> },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: <Settings />,
    adminOnly: true,
    children: [
      { to: '/admin/approvals', label: 'Approvals', icon: <UserCheck /> },
      { to: '/admin/audit', label: 'Audit log', icon: <FileText /> },
      { to: '/admin/invites', label: 'Invites', icon: <Mail /> },
      { to: '/users', label: 'Users', icon: <Users /> },
    ],
  },
];
